import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const LoginModal = () => {
  const { 
    isLoginModalOpen, 
    setIsLoginModalOpen, 
    isRegisterMode, 
    setIsRegisterMode,
    login, 
    register, confirmRegistration
  } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: ''
  });
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [pending, setPending] = useState(null);
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(0);
  useEffect(() => {
    if (!cooldown) return;
    const timer = setTimeout(() => setCooldown(v => Math.max(0, v - 1)), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);
  useEffect(() => {
    if (!isLoginModalOpen) { setPending(null); setCode(''); setError(''); setFormData(v => ({ ...v, password: '' })); }
  }, [isLoginModalOpen]);
  const resend = async () => {
    setIsLoading(true); setError('');
    try {
      const result = await register(formData.email, formData.password, formData.name, formData.phone);
      if (result.success) { setPending(result); setCode(''); setCooldown(60); }
      else setError(result.error);
    } finally { setIsLoading(false); }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (isRegisterMode && !consent) { setError('Подтвердите согласие на обработку данных для регистрации.'); return; }
    setIsLoading(true);

    try {
      let result;
      if (pending) {
        result = await confirmRegistration(pending.challenge_id, code);
      } else if (isRegisterMode) {
        result = await register(formData.email, formData.password, formData.name, formData.phone);
      } else {
        result = await login(formData.email, formData.password);
      }

      if (result.verification_required) {
        setPending(result); setCode(''); setCooldown(60); return;
      }
      if (result.success) {
        setIsLoginModalOpen(false);
        setFormData({ email: '', password: '', name: '', phone: '' });
        setIsRegisterMode(false);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Произошла ошибка');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMode = () => {
    setPending(null); setCode('');
    setIsRegisterMode(!isRegisterMode);
    setConsent(false);
    setError('');
  };

  return (
    <Dialog open={isLoginModalOpen} onOpenChange={setIsLoginModalOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center text-xl font-medium">
            {pending ? 'Подтвердите email' : isRegisterMode ? 'Регистрация' : 'Вход в аккаунт'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4 ym-disable-keys">
          {pending ? <div className="space-y-4">
            <p className="text-sm">Отправили код на {pending.email}. Введите 6 цифр из письма. Код действует 10 минут. Если письма нет, проверьте папку «Спам».</p>
            <Label htmlFor="verification-code">Код из письма</Label>
            <Input id="verification-code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} required autoFocus />
            <button type="button" disabled={isLoading || cooldown > 0} onClick={resend} className="text-sm underline disabled:opacity-50">{cooldown ? `Отправить повторно через ${cooldown} с` : 'Отправить код повторно'}</button>
            <button type="button" disabled={isLoading} onClick={() => { setPending(null); setCode(''); setError(''); }} className="block text-sm underline">Изменить email</button>
          </div> : <>
          {isRegisterMode && (
            <>
              <div className="space-y-2">
                <Label htmlFor="name">ФИО</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ваше ФИО"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Телефон</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+7 999 123 45 67"
                />
              </div>
            </>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="Введите email"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Пароль</Label>
            <Input
              id="password"
              type="password"
              minLength={isRegisterMode ? 8 : undefined}
              maxLength={128}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Введите пароль"
              required
            />
          </div>
          {isRegisterMode && <label className="flex items-start gap-3 text-sm"><input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} required className="mt-1 accent-[#AD2831]" /><span>Даю <a href="/data-consent" target="_blank" rel="noreferrer" className="underline">согласие на обработку персональных данных</a> для аккаунта. <a href="/privacy_policy" target="_blank" rel="noreferrer" className="underline">Политика</a>.</span></label>}
          </>}
          {error && (
            <p className="text-brand-text text-sm">{error}</p>
          )}
          <Button
            type="submit"
            className="w-full h-11 bg-brand-surface text-brand-text hover:bg-brand-surface border border-brand-border"
            disabled={isLoading}
          >
            {isLoading ? 'Подождите...' : pending ? 'Подтвердить и зарегистрироваться' : (isRegisterMode ? 'Получить код на email' : 'Войти')}
          </Button>
          <p className="text-center text-sm text-brand-text">
            {isRegisterMode ? 'Уже есть аккаунт?' : 'Нет аккаунта?'}{' '}
            <button
              type="button"
              disabled={isLoading}
              onClick={toggleMode}
              className="text-gray underline hover:no-underline"
            >
              {isRegisterMode ? 'Войти' : 'Зарегистрироваться'}
            </button>
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LoginModal;
